/**
 * Order status utilities for role-based status transitions
 * @format
 */

import type { Order } from '../types/orders.types';
import { UserRole } from '../../auth/type';
// import { Permission, roleHasPermission } from '../../auth/permissions';

// Order status type
type OrderStatus = Order['status'];

// Status categories
export const PRE_CONFIRMATION_STATUSES: OrderStatus[] = ["PENDING"];
export const CONFIRMATION_STATUS: OrderStatus = 'ORDER_CONFIRMED';
export const FULFILMENT_STATUSES: OrderStatus[] = [
  "PROCESSING",
  "EN_ROUTE",
  "DELIVERED",
];
export const TERMINAL_STATUSES: OrderStatus[] = ['CANCELLED', 'REFUNDED', 'TRANSFER_FAILED', 'RETURNED'];

// All statuses in required display order
export const STATUS_FLOW: OrderStatus[] = [
  "ORDER_RECEIVED",
  "ORDER_CONFIRMED",
  "PROCESSING",
  "EN_ROUTE",
  "DELIVERED",
  "CANCELLED",
  "REFUNDED",
  "TRANSFER_FAILED",
  "RETURNED",
];

/**
 * Check if a status is a pre-confirmation status
 */
export const isPreConfirmationStatus = (status: OrderStatus): boolean => {
  return PRE_CONFIRMATION_STATUSES.includes(status);
};

/**
 * Check if a status is a fulfilment status
 */
export const isFulfilmentStatus = (status: OrderStatus): boolean => {
  return FULFILMENT_STATUSES.includes(status);
};

/**
 * Check if a status is a terminal status (cannot be changed)
 */
export const isTerminalStatus = (status: OrderStatus): boolean => {
  return TERMINAL_STATUSES.includes(status);
};

/**
 * Get allowed target statuses for a user based on their role and current order status.
 * Only forward transitions are allowed — no reverting to a previous status.
 */
export const getAllowedStatusTransitions = (
  currentStatus: OrderStatus,
  role?: UserRole | undefined,
): OrderStatus[] => {
  if (!role || isTerminalStatus(currentStatus)) {
    return [];
  }

  const currentIndex = STATUS_FLOW.indexOf(currentStatus);

  // Only allow statuses that come after the current one in the flow
  // plus terminal statuses (CANCELLED, REFUNDED, TRANSFER_FAILED) if not already terminal
  return STATUS_FLOW.filter((status, index) => {
    if (TERMINAL_STATUSES.includes(status)) {
      return true // terminal statuses always available as an option
    }
    return index > currentIndex
  }).filter(status => status !== currentStatus) as OrderStatus[];
};

/**
 * Check if a user can update an order's status to a specific target status
 */
export const canUserUpdateToStatus = (
  currentStatus: OrderStatus,
  targetStatus: OrderStatus,
  role: UserRole | undefined
): boolean => {
  const allowedStatuses = getAllowedStatusTransitions(currentStatus, role);
  return allowedStatuses.includes(targetStatus);
};

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status: OrderStatus): string => {
  const labels: Record<OrderStatus, string> = {
    PENDING: "Pending",
    // PAID: 'Paid',
    ORDER_CONFIRMED: "Order Confirmed",
    ORDER_RECEIVED: "Order Received",
    PROCESSING: "Processing",
    EN_ROUTE: "En Route",
    DELIVERED: "Delivered",
    CANCELLED: "Cancelled",
    REFUNDED: "Refunded",
    TRANSFER_FAILED: "Transfer Failed",
    RETURNED: "Returned",
  };
  return labels[status] || status;
};
